import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import type {
    CatalogStatus,
    Category,
    CurrencyConfig,
    FieldErrors,
    Product
} from '@/types'

import { toast } from 'sonner'
import { useId, useState } from 'react'

import {
    CircleX,
    Info,
    LoaderCircle,
    Plus,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatMoney, formatPercent } from '@/lib/format'
import { ApiError, apiRequest, getErrorMessage } from '@/lib/api'
import { parseDecimal, validateCategory, validateProduct } from '@/lib/validation'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { useSettings } from '@/components/generals/settings-provider'
import FormField, { describedBy } from '@/components/generals/form-field'
import UnitLabel from '@/components/inventory/unit-label'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    CATEGORY_COLORS,
    DEFAULT_UNIT,
    LIMITS,
    UNIT_SUGGESTIONS
} from '@/constants/inventory'

interface ProductFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    requestKey: number
    product: Product | null
    categories: Category[]
    catalogStatus: CatalogStatus
    onCategoryCreated: (category: Category) => void
    onSaved: (product: Product, mode: 'create' | 'update') => void
}

type ProductFormValues = {
    name: string
    sku: string
    categoryId: string
    unit: string
    initialStock: string
    minStock: string
    cost: string
    price: string
    location: string
    supplier: string
}

type ProductFormProps = Omit<ProductFormDialogProps, 'open' | 'requestKey'> & {
    baseId: string
}

const FIELD_ORDER: (keyof ProductFormValues)[] = [
    'name',
    'sku',
    'categoryId',
    'unit',
    'location',
    'initialStock',
    'minStock',
    'cost',
    'price',
    'supplier'
]

const toInputDecimal = (value: number, currency: CurrencyConfig): string => {
    if (!value) return ''

    const cents = value * 100
    const text = Math.abs(cents - Math.round(cents)) < 1e-6
        ? (Number.isInteger(value) && currency.decimals === 0 ? String(value) : value.toFixed(2))
        : String(value)

    return text.replace('.', currency.decimalSeparator)
}

const toFormValues = (product: Product | null, currency: CurrencyConfig): ProductFormValues => ({
    name: product?.name ?? '',
    sku: product?.sku ?? '',
    categoryId: product?.categoryId ?? '',
    unit: product?.unit ?? DEFAULT_UNIT,
    initialStock: '',
    minStock: product ? String(product.minStock) : '',
    cost: product ? toInputDecimal(product.cost, currency) : '',
    price: product ? toInputDecimal(product.price, currency) : '',
    location: product?.location ?? '',
    supplier: product?.supplier ?? ''
})

const ProductForm: FC<ProductFormProps> = ({
    baseId,
    product,
    categories,
    catalogStatus,
    onCategoryCreated,
    onSaved
}): ReactNode => {
    const { currency, number } = useSettings()
    const mode = product ? 'update' : 'create'
    const fieldId = (field: string) => `${baseId}-${field}`

    const [values, setValues] = useState<ProductFormValues>(() => toFormValues(product, currency))
    const [errors, setErrors] = useState<FieldErrors>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const [creatingCategory, setCreatingCategory] = useState(false)
    const [newCategoryName, setNewCategoryName] = useState('')
    const [categoryError, setCategoryError] = useState<string | null>(null)
    const [savingCategory, setSavingCategory] = useState(false)

    const update = (field: keyof ProductFormValues) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { value } = event.target

        setValues(previous => ({ ...previous, [field]: value }))
        setErrors(previous => (previous[field] ? { ...previous, [field]: undefined } : previous))
    }

    const fieldProps = (field: keyof ProductFormValues, hint?: string) => ({
        id: fieldId(field),
        value: values[field],
        onChange: update(field),
        'aria-invalid': Boolean(errors[field]) || undefined,
        'aria-describedby': describedBy(fieldId(field), errors[field], hint)
    })

    const focusFirstError = (fieldErrors: FieldErrors) => {
        const first = FIELD_ORDER.find(field => fieldErrors[field])

        if (first) requestAnimationFrame(() => document.getElementById(fieldId(first))?.focus())
    }

    const handleCreateCategory = async () => {
        const color = CATEGORY_COLORS[categories.length % CATEGORY_COLORS.length]
        const result = validateCategory({ name: newCategoryName, color })

        if (!result.ok) {
            setCategoryError(result.errors.name ?? 'Revisa el nombre de la categoría.')

            return
        }

        setSavingCategory(true)
        setCategoryError(null)

        try {
            const { category } = await apiRequest<{ category: Category }>('/api/categories', {
                method: 'POST',
                body: result.value
            })

            onCategoryCreated(category)
            setValues(previous => ({ ...previous, categoryId: category.id }))
            setCreatingCategory(false)
            setNewCategoryName('')
            toast.success(`Categoría «${category.name}» creada`)
        }
        catch (error) {
            setCategoryError(error instanceof ApiError ? error.fieldErrors.name ?? error.message : getErrorMessage(error))
        }
        finally {
            setSavingCategory(false)
        }
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setFormError(null)

        const result = validateProduct(values, mode)

        if (!result.ok) {
            setErrors(result.errors)
            focusFirstError(result.errors)

            return
        }

        setErrors({})
        setSubmitting(true)

        try {
            const response = await apiRequest<{ product: Product }>(product ? `/api/products/${product.id}` : '/api/products', {
                method: product ? 'PUT' : 'POST',
                body: result.value
            })

            onSaved(response.product, mode)
        }
        catch (error) {
            if (error instanceof ApiError) {
                setErrors(error.fieldErrors)
                focusFirstError(error.fieldErrors)

                if (!Object.keys(error.fieldErrors).length) setFormError(error.message)
            }
            else {
                setFormError(getErrorMessage(error))
            }
        }
        finally {
            setSubmitting(false)
        }
    }

    const cost = parseDecimal(values.cost)
    const price = parseDecimal(values.price)
    const hasMargin = typeof cost === 'number' && typeof price === 'number' && !Number.isNaN(cost) && !Number.isNaN(price) && price > 0
    const margin = hasMargin ? (price as number) - (cost as number) : 0
    const marginPercent = hasMargin ? (margin / (price as number)) * 100 : 0
    const symbolPadding = currency.symbol.length > 1 ? 'pl-12' : 'pl-8'
    const symbolAfterPadding = currency.symbol.length > 1 ? 'pr-12' : 'pr-8'

    const categoryMissing = Boolean(product?.categoryId) && !categories.some(category => category.id === product?.categoryId)

    const moneyInput = (field: 'cost' | 'price') => (
        <div className='relative'>
            <Input
                {...fieldProps(field, field === 'cost' ? 'Lo que te cuesta cada unidad.' : hasMargin ? 'margen' : undefined)}
                inputMode='decimal'
                autoComplete='off'
                placeholder={currency.decimals ? `0${currency.decimalSeparator}00` : '0'}
                className={cn('tabular-nums', currency.symbolPosition === 'before' ? symbolPadding : symbolAfterPadding)}
            />

            <span
                className={cn(
                    'pointer-events-none absolute inset-y-0 flex items-center text-sm text-muted-foreground',
                    currency.symbolPosition === 'before' ? 'left-3' : 'right-3'
                )}
                aria-hidden='true'
            >
                {currency.symbol}
            </span>
        </div>
    )

    return (
        <form onSubmit={handleSubmit} noValidate className='flex min-h-0 flex-1 flex-col'>
            <DialogHeader className='border-b px-5 py-4 pr-12'>
                <DialogTitle>
                    {product ? 'Editar producto' : 'Nuevo producto'}
                </DialogTitle>

                <DialogDescription>
                    {product ? 'Actualiza los datos del producto. El stock se cambia con movimientos.' : 'Añade un artículo a tu inventario con su stock de partida.'}
                </DialogDescription>
            </DialogHeader>

            <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5'>
                {formError && (
                    <div role='alert' className='flex items-start gap-2 rounded-lg bg-danger-soft px-3 py-2.5 text-sm text-danger-ink'>
                        <CircleX className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                        {formError}
                    </div>
                )}

                <FormField id={fieldId('name')} label='Nombre del producto' error={errors.name}>
                    <Input
                        {...fieldProps('name')}
                        maxLength={LIMITS.name}
                        autoComplete='off'
                        placeholder='Ej.: Cable USB-C de 1 m'
                    />
                </FormField>

                <div className='grid gap-4 sm:grid-cols-2'>
                    <FormField id={fieldId('sku')} label='Código o SKU' optional error={errors.sku}>
                        <Input
                            {...fieldProps('sku')}
                            maxLength={LIMITS.sku}
                            autoComplete='off'
                            placeholder='Ej.: ELE-005'
                            className='font-mono'
                        />
                    </FormField>

                    <FormField id={fieldId('categoryId')} label='Categoría' error={errors.categoryId}>
                        <div className='flex items-center gap-2'>
                            <NativeSelect
                                id={fieldId('categoryId')}
                                value={values.categoryId}
                                onChange={update('categoryId')}
                                aria-invalid={Boolean(errors.categoryId) || undefined}
                                className='w-full min-w-0 flex-1'
                            >
                                <NativeSelectOption value=''>
                                    Sin categoría
                                </NativeSelectOption>

                                {categoryMissing && product?.categoryId && (
                                    <NativeSelectOption value={product.categoryId}>
                                        {product.categoryName ?? 'Categoría actual'}
                                    </NativeSelectOption>
                                )}

                                {categories.map(category => (
                                    <NativeSelectOption key={category.id} value={category.id}>
                                        {category.name}
                                    </NativeSelectOption>
                                ))}
                            </NativeSelect>

                            <Button
                                type='button'
                                variant='outline'
                                size='icon'
                                aria-label={creatingCategory ? 'Cancelar nueva categoría' : 'Crear una categoría nueva'}
                                aria-expanded={creatingCategory}
                                onClick={() => {
                                    setCreatingCategory(value => !value)
                                    setCategoryError(null)
                                }}
                            >
                                {creatingCategory ? <X aria-hidden='true' /> : <Plus aria-hidden='true' />}
                            </Button>
                        </div>

                        {catalogStatus === 'loading' && !categories.length && (
                            <p className='text-xs text-muted-foreground'>
                                Cargando categorías…
                            </p>
                        )}
                    </FormField>
                </div>

                {creatingCategory && (
                    <div className='flex flex-col gap-2 rounded-lg border border-dashed bg-muted/40 p-3'>
                        <label htmlFor={fieldId('new-category')} className='text-[0.8rem] font-medium'>
                            Nueva categoría
                        </label>

                        <div className='flex gap-2'>
                            <Input
                                id={fieldId('new-category')}
                                value={newCategoryName}
                                maxLength={LIMITS.categoryName}
                                placeholder='Ej.: Herramientas'
                                autoFocus
                                onChange={event => {
                                    setNewCategoryName(event.target.value)
                                    setCategoryError(null)
                                }}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault()
                                        void handleCreateCategory()
                                    }
                                }}
                                aria-invalid={Boolean(categoryError) || undefined}
                                aria-describedby={categoryError ? fieldId('new-category-error') : undefined}
                            />

                            <Button type='button' onClick={handleCreateCategory} disabled={savingCategory}>
                                {savingCategory && <LoaderCircle className='animate-spin' aria-hidden='true' />}
                                Crear
                            </Button>
                        </div>

                        {categoryError && (
                            <p id={fieldId('new-category-error')} className='text-xs text-destructive'>
                                {categoryError}
                            </p>
                        )}
                    </div>
                )}

                <div className='grid gap-4 sm:grid-cols-2'>
                    <FormField
                        id={fieldId('unit')}
                        label='Unidad de medida'
                        hint='Cómo cuentas este producto: ud., caja, kg…'
                        error={errors.unit}
                    >
                        <Input
                            {...fieldProps('unit', 'Cómo cuentas este producto: ud., caja, kg…')}
                            list={fieldId('unit-options')}
                            maxLength={LIMITS.unit}
                            autoComplete='off'
                        />

                        <datalist id={fieldId('unit-options')}>
                            {UNIT_SUGGESTIONS.map(unit => (
                                <option key={unit} value={unit} />
                            ))}
                        </datalist>
                    </FormField>

                    <FormField id={fieldId('location')} label='Ubicación' optional error={errors.location}>
                        <Input
                            {...fieldProps('location')}
                            maxLength={LIMITS.location}
                            autoComplete='off'
                            placeholder='Ej.: Estantería A1'
                        />
                    </FormField>
                </div>

                <div className='grid gap-4 sm:grid-cols-2'>
                    {product ? (
                        <div className='flex flex-col gap-1.5'>
                            <span className='text-[0.8rem] font-medium'>
                                Stock actual
                            </span>

                            <div className='flex h-9 items-center rounded-lg border border-dashed bg-muted/40 px-3 text-sm font-semibold tabular-nums'>
                                <span>{number(product.quantity)}</span>&nbsp;<UnitLabel productId={product.id} unit={product.unit} className='font-normal text-muted-foreground' />
                            </div>
                        </div>
                    ) : (
                        <FormField
                            id={fieldId('initialStock')}
                            label='Stock inicial'
                            hint='Unidades que tienes ahora mismo.'
                            error={errors.initialStock}
                        >
                            <Input
                                {...fieldProps('initialStock', 'Unidades que tienes ahora mismo.')}
                                inputMode='numeric'
                                pattern='[0-9]*'
                                autoComplete='off'
                                placeholder='0'
                                className='tabular-nums'
                            />
                        </FormField>
                    )}

                    <FormField
                        id={fieldId('minStock')}
                        label='Stock mínimo'
                        hint='Te avisaremos al llegar a esta cifra.'
                        error={errors.minStock}
                    >
                        <Input
                            {...fieldProps('minStock', 'Te avisaremos al llegar a esta cifra.')}
                            inputMode='numeric'
                            pattern='[0-9]*'
                            autoComplete='off'
                            placeholder='0'
                            className='tabular-nums'
                        />
                    </FormField>
                </div>

                {product && (
                    <p className='-mt-2 flex items-start gap-2 text-xs text-muted-foreground'>
                        <Info className='mt-px size-3.5 shrink-0' aria-hidden='true' />
                        Para cambiar el stock, registra una entrada, una salida o un ajuste desde el inventario.
                    </p>
                )}

                <div className='grid gap-4 sm:grid-cols-2'>
                    <FormField id={fieldId('cost')} label='Precio de compra' hint='Lo que te cuesta cada unidad.' error={errors.cost}>
                        {moneyInput('cost')}
                    </FormField>

                    <FormField
                        id={fieldId('price')}
                        label='Precio de venta'
                        optional
                        error={errors.price}
                        hint={hasMargin ? `Margen: ${formatMoney(margin, currency)} por unidad (${formatPercent(marginPercent, currency)})` : undefined}
                    >
                        {moneyInput('price')}
                    </FormField>
                </div>

                <FormField id={fieldId('supplier')} label='Proveedor' optional error={errors.supplier}>
                    <Input
                        {...fieldProps('supplier')}
                        maxLength={LIMITS.supplier}
                        autoComplete='off'
                        placeholder='Ej.: Distribuciones Tecnova'
                    />
                </FormField>
            </div>

            <DialogFooter className='mx-0 mb-0 rounded-b-xl px-5 py-4'>
                <DialogClose render={<Button type='button' variant='outline' />}>
                    Cancelar
                </DialogClose>

                <Button type='submit' disabled={submitting}>
                    {submitting && <LoaderCircle className='animate-spin' aria-hidden='true' />}
                    {submitting ? 'Guardando…' : product ? 'Guardar cambios' : 'Crear producto'}
                </Button>
            </DialogFooter>
        </form>
    )
}

const ProductFormDialog: FC<ProductFormDialogProps> = ({ open, requestKey, onOpenChange, ...props }): ReactNode => {
    const baseId = useId()

    return (
        <Dialog open={open} onOpenChange={nextOpen => onOpenChange(nextOpen)}>
            <DialogContent
                className='flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl'
                initialFocus={() => document.getElementById(`${baseId}-name`) ?? true}
            >
                <ProductForm key={requestKey} baseId={baseId} onOpenChange={onOpenChange} {...props} />
            </DialogContent>
        </Dialog>
    )
}

export default ProductFormDialog
